import { useLayoutSearchParams, useScopedPathname } from "@-/com.npmjs.expo-router";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useStudySession } from "@-/dev.improve.haniverse.lib.study-time";
import { LearnLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import type { PropsWithChildren } from "react";

const STUDY_ROUTE = /\/learn\/(deck|lesson|drill|review)(\/|$)/u;

export function StudySessionBoundary({ children }: PropsWithChildren) {
  // Only Learn's own routes matter here: a session runs only while Learn is focused.
  const pathname = useScopedPathname("/learn");
  // The legacy slide route names its slide in the route; deck steps come from the deck itself.
  const params = useLayoutSearchParams<{ slideSlug?: string }>();
  const focused = useIsFocused();
  const [card, step, input] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.activeDeckCard,
    (c) => c.activeDeckStep,
    (c) => c.answerInputState.input,
  );
  const isStudyRoute = STUDY_ROUTE.test(pathname) && !pathname.endsWith("/done");
  // A deck counts once it shows a step. Each reveal, rating or Undo changes the step, which counts
  // as activity even without a touch (voice study).
  const ready = !pathname.endsWith("/deck") || step !== null;
  const onActivity = useStudySession(
    focused && isStudyRoute && ready,
    `${pathname}:${JSON.stringify(card)}:${params.slideSlug}:${step}:${input}`,
  );
  return (
    <View className="flex-1" onTouchMove={onActivity} onTouchStart={onActivity}>
      {children}
    </View>
  );
}
